import React, { Component } from "react";
import { Route, Switch } from "react-router-dom";
import { withRouter } from "react-router";
import PropTypes from "prop-types";
import { fetchBooksAndChapters, fetchBibles } from "../../api/BibleApi";
import Chapter from "../Chapter/ChapterContainer";
import SearchResults from "../SearchResults/SearchResults";
import { isTabletDevice, isMobileDevice } from "../../utils/mobile";
import "scripture-styles/dist/css/scripture-styles.css";
import "../../assets/stylesheets/scripture.css";

import {
  getParallels,
  changeBible,
  removeAllParallels,
  removeParallel,
  addParallel
} from "../../utils/paths";

const BibleContext = React.createContext();

class BibleContainer extends Component {
  static Consumer = BibleContext.Consumer;
  static propTypes = {
    site: PropTypes.shape({
      bibles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired
        })
      )
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        bibleId: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired
    }).isRequired
  };

  UNSAFE_componentWillMount = async () => {
    const { match, location, site } = this.props;
    const { bibleId } = match.params;
    const parallelBibleIds = getParallels(location.search);
    const siteBibleIds = site ? site.bibles.map(bible => bible.id) : [];

    this.setState({
      currentBibleId: bibleId,
      siteBibleIds,
      parallelBibleIds,
      biblesById: site.bibles.reduce((acc, bible) => {
        acc[bible.id] = {
          ...bible,
          audioBibles: []
        };
        return acc;
      }, {})
    });

    this.loadBibles(siteBibleIds, site.includeAllBibles);
    this.loadBooksAndChapters(bibleId);
  };

  componentDidMount() {
    // NOTE: The "orientationchange" event is fired before the "resize" event
    // takes place. In order to be able to have getMaxNumParallels to return
    // the correct value we need to nest the "resize" event inside of the
    // "orientation" event in order to capture the "resize" event that happens
    // directly after the "orientationchange".
    window.addEventListener("orientationchange", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("orientationchange", this.handleResize);
    window.removeEventListener("resize", this.removeExcessParallels);
  }

  handleResize = () => {
    window.addEventListener("resize", this.removeExcessParallels);
  };

  removeExcessParallels = () => {
    const currentParallelCount = this.state.parallelBibleIds.length;
    const maxParallelCount = this.getMaxNumParallels();
    if (currentParallelCount > maxParallelCount) {
      this.props.history.push(
        removeAllParallels({ location: this.props.location })
      );
    }
  };

  componentDidUpdate = async (prevProps, prevState) => {
    const { bibleId } = this.props.match.params;
    const parallelBibleIds = getParallels(this.props.location.search);

    const newState = {};
    let shouldSetState = false;

    if (bibleId !== prevProps.match.params.bibleId) {
      shouldSetState = true;
      newState.currentBibleId = bibleId;
      this.loadBooksAndChapters(bibleId);
    }

    if (parallelBibleIds.join(",") !== prevState.parallelBibleIds.join(",")) {
      shouldSetState = true;
      newState.parallelBibleIds = parallelBibleIds;
    }

    if (shouldSetState) {
      // console.log("updating bible state");
      this.setState(newState);
    }
  };

  getCurrentBible = () => this.state.biblesById[this.state.currentBibleId];

  getParallelBibles = () => {
    this.state.parallelBibleIds
      .map(id => this.state.biblesById[id])
      .filter(id => id);
  };

  getMaxNumParallels = () => {
    if (isTabletDevice()) return 1;
    else if (isMobileDevice()) return 0;
    return 2;
  };

  getBibles = () => {
    return Object.keys(this.state.biblesById)
      .map(id => this.state.biblesById[id])
      .filter(id => id);
  };

  bibleHasBooks = bibleId => {
    const bible = this.state.biblesById[bibleId];
    return bible && bible.books;
  };

  loadBibles = async (bibleIds, includeAllBibles) => {
    const { data } = await fetchBibles(bibleIds, includeAllBibles);

    this.setState(prevState => {
      const biblesById = data.reduce((acc, bible) => {
        acc[bible.id] = {
          ...prevState.biblesById[bible.id],
          ...bible
        };
        return acc;
      }, {});
      return { biblesById };
    });
  };

  buildChapterIdArray({ books }) {
    return books.reduce((acc, book) => {
      return acc.concat(
        book.chapters
          .sort((a, b) => a.position - b.position)
          .map(chapter => chapter.id)
      );
    }, []);
  }

  loadBooksAndChapters = async bibleId => {
    if (this.bibleHasBooks(bibleId)) {
      return;
    }
    const { data } = await fetchBooksAndChapters({ bibleId });
    const { biblesById } = this.state;

    this.setState(prevState => ({
      biblesById: {
        ...prevState.biblesById,
        [bibleId]: {
          ...biblesById[bibleId],
          books: data,
          orderedChapterIds: this.buildChapterIdArray({
            books: data
          })
        }
      }
    }));
  };

  handleBibleSelect = (bibleId, parallelIndex) => {
    this.props.history.push(
      changeBible({
        match: this.props.match,
        location: this.props.location,
        bibleId,
        parallelIndex
      })
    );
  };

  handleAddParallel = () => {
    const maxNumParallels = this.getMaxNumParallels();
    // console.log("maxNumParallels: ", maxNumParallels);

    const { currentBibleId, parallelBibleIds } = this.state;
    if (maxNumParallels > parallelBibleIds.length) {
      this.props.history.push(
        addParallel({
          parallelId: currentBibleId,
          location: this.props.location
        })
      );
    }
  };

  handleRemoveParallel = parallelIndex => {
    this.props.history.push(
      removeParallel({ parallelIndex, location: this.props.location })
    );
  };

  state = {
    biblesById: {},
    siteBibleIds: [],
    parallelBibleIds: [],
    currentBibleId: null,
    loadBibles: this.loadBibles,
    getBibles: this.getBibles,
    getParallelBibles: this.getParallelBibles,
    onBibleSelect: this.handleBibleSelect,
    onAddParallel: this.handleAddParallel,
    onRemoveParallel: this.handleRemoveParallel,
    getCurrentBible: this.getCurrentBible
  };

  render() {
    const { currentBibleId } = this.state;

    return (
      <BibleContext.Provider value={this.state}>
        <Switch>
          <Route
            path="/bible/:bibleId/search"
            render={() => <SearchResults bibleId={currentBibleId} />}
          />
          <Route path="/bible/:bibleId/:chapterId" render={() => <Chapter />} />
        </Switch>
      </BibleContext.Provider>
    );
  }
}

export default withRouter(BibleContainer);

export function withBible(WrappedComponent) {
  return function ComponentWithBible(props) {
    return (
      <BibleContainer.Consumer>
        {context => <WrappedComponent {...props} bible={context} />}
      </BibleContainer.Consumer>
    );
  };
}
